// Mahnläufe (/rechnung/mahnlauf): Antworten des Servers (server/src/modules/rechnungstool/mahnlauf-routes.ts) und das
// Formular der Einstellung. Beträge in Euro; die Gründe fürs Auslassen kommen als Text vom Server.
import { parseEuro } from '../lib/money';
import { dePrice } from './formular';
import type { Waehrung } from './waehrung';

export type Modus = 'vorschlagen' | 'senden';
export type Stufe = 1 | 2 | 3;

export interface StufeEinstellung {
  tage: number;
  frist: number;
  gebuehr: number;
  zinsenPrivat: boolean;
  pauschale: boolean;
  zinsenFirma: boolean;
}

export interface MahnlaufEinstellung {
  an: boolean;
  modus: Modus;
  bisStufe: Stufe;
  stufen: [StufeEinstellung, StufeEinstellung, StufeEinstellung];
  abBetrag: number;
  bericht: boolean;
  // eingeschaltet, aber der Tarif enthält die Mahnläufe nicht (mehr)
  pausiert: boolean;
}

// heute fällig: diese Stufe, an diese Adresse
export interface Kandidat {
  rechnungId: string;
  nummer: string;
  kunde: string;
  kundeId: string | null;
  stufe: Stufe;
  titel: string;
  faelligAm: string;
  ab: string;
  offen: number;
  an: string;
}

// fällig, aber nicht automatisch (text: warum)
export interface Ausgelassen {
  rechnungId: string;
  nummer: string;
  kunde: string;
  stufe: Stufe | null;
  titel: string | null;
  ab: string | null;
  offen: number;
  // Währung von offen (fehlt: Euro)
  waehrung?: Waehrung;
  grund: string;
  text: string;
  // letzte Mahnung (für „Als verschickt markieren“)
  mahnungId: string | null;
}

// Mahnung aus dem Mahnlauf, deren E-Mail nicht rausging (offen: seit 15 Minuten hängen geblieben, z. B. nach einem
// Neustart)
export interface Problem {
  mahnungId: string;
  rechnungId: string;
  nummer: string;
  kunde: string;
  stufe: Stufe;
  titel: string;
  status: 'offen' | 'fehler' | 'aufgegeben';
  fehler: string | null;
  versuche: number;
  letzterVersuch: string | null;
  // Mahnstopp an der Rechnung oder beim Kunden: kein weiterer Versuch
  mahnstopp: boolean;
}

export interface Lauf {
  tag: string;
  modus: Modus;
  status: 'laeuft' | 'fertig' | 'pausiert' | 'fehler';
  faellig: number;
  geschrieben: number;
  gesendet: number;
  fehler: number;
  uebersprungen: number;
  hinweis: string | null;
}

export interface MahnlaufAntwort {
  einstellung: MahnlaufEinstellung;
  // Billtoro ohne die Mahnläufe im Tarif
  gesperrt: boolean;
  heute: string;
  naechsterLauf: 'heute' | 'morgen';
  faellig: Kandidat[];
  ausgelassen: Ausgelassen[];
  probleme: Problem[];
  laeufe: Lauf[];
}

export interface SendenErgebnis {
  ergebnisse: { rechnungId: string; nummer: string | null; stufe: Stufe; status: 'gesendet' | 'fehler' | 'uebersprungen'; text?: string; mahnungId?: string }[];
  gesendet: number;
  fehler: number;
}

export const mahnlaufUrl = (firmId: string) => `/api/rechnungstool/${firmId}/mahnlauf`;
export const erledigtUrl = (mahnungId: string) => `/api/rechnungstool/mahnung/${mahnungId}/mahnlauf-erledigt`;
export const mahnstoppUrl = (rechnungId: string) => `/api/rechnungstool/rechnung/${rechnungId}/mahnstopp`;
// so viele mit einem Klick (wie der Server)
export const MAX_LISTE = 25;
// so oft versucht der Mahnlauf eine E-Mail
export const MAX_VERSUCHE = 3;
export const mahnungen = (n: number) => (n === 1 ? '1 Mahnung' : `${n} Mahnungen`);

// --- Formular ---

export interface StufeForm {
  tage: string;
  frist: string;
  gebuehr: string;
  zinsenPrivat: boolean;
  pauschale: boolean;
  zinsenFirma: boolean;
}

export interface EinstellungForm {
  an: boolean;
  modus: Modus;
  bisStufe: Stufe;
  stufen: [StufeForm, StufeForm, StufeForm];
  abBetrag: string;
  bericht: boolean;
}

const stufeForm = (s: StufeEinstellung): StufeForm => ({
  tage: String(s.tage),
  frist: String(s.frist),
  gebuehr: dePrice(s.gebuehr),
  zinsenPrivat: s.zinsenPrivat,
  pauschale: s.pauschale,
  zinsenFirma: s.zinsenFirma,
});

export const formAus = (e: MahnlaufEinstellung): EinstellungForm => ({
  an: e.an,
  modus: e.modus,
  bisStufe: e.bisStufe,
  stufen: [stufeForm(e.stufen[0]), stufeForm(e.stufen[1]), stufeForm(e.stufen[2])],
  abBetrag: dePrice(e.abBetrag),
  bericht: e.bericht,
});

// ganze Tage; sonst null (der Server sagt dann, was falsch ist)
const tage = (t: string) => (/^\d{1,3}$/.test(t.trim()) ? Number(t.trim()) : null);
const betrag = (t: string) => parseEuro(t) ?? null;

const stufeBody = (s: StufeForm) => ({
  tage: tage(s.tage),
  frist: tage(s.frist),
  gebuehr: betrag(s.gebuehr),
  zinsenPrivat: s.zinsenPrivat,
  pauschale: s.pauschale,
  zinsenFirma: s.zinsenFirma,
});

// Body für PUT …/mahnlauf; geprüft wird auf dem Server
export const bodyAus = (f: EinstellungForm) => ({
  an: f.an,
  modus: f.modus,
  bisStufe: f.bisStufe,
  stufen: f.stufen.map(stufeBody),
  abBetrag: betrag(f.abBetrag),
  bericht: f.bericht,
});
